import { cn } from '@ngg/storefront-utils';
import React, { HTMLProps, useCallback, useMemo, useRef } from 'react';
import { formatPriceParts } from '@utils/currency/formatPriceParts';
import config from '@utils/config';

const REGEXP_TRAILING_ZEROS = /^0+/;

const removeLeadingZero = (str: string) =>
  str.length > 1 ? str.replace(REGEXP_TRAILING_ZEROS, '') : str;

type Props<T> = T & {
  value: number;
  labelClassName?: string;
  alignLabel?: 'left' | 'right';
};

export default function NumberInput(props: Props<HTMLProps<HTMLInputElement>>) {
  const {
    id,
    max,
    min,
    name,
    label,
    className,
    labelClassName,
    alignLabel = 'left',
    value,
  } = props;
  const { locale } = config;

  const inputRef = useRef<HTMLInputElement>(null);

  const validateChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        if (inputRef.current === null) {
          return;
        }

        if (inputRef.current.value === '') {
          inputRef.current.placeholder = '';
        }

        inputRef.current.value = removeLeadingZero(event.target.value);
      },
      [inputRef],
    );

  const displayValue = useMemo(() => formatPriceParts(value), [value]);

  return (
    <>
      <label
        htmlFor={id}
        className={cn(
          'text-xxs',
          alignLabel === 'left' ? 'place-self-start' : 'place-self-end',
          labelClassName,
        )}>
        {label}
      </label>
      <div
        className={cn(
          'relative flex items-center justify-center transition-all ease-out',
        )}>
        <input
          defaultValue={value}
          name={name}
          ref={inputRef}
          id={id}
          type="number"
          className={cn(
            'peer block w-full p-1 placeholder-black',
            'text-center text-sm text-transparent',
            'border border-gray-600',
            'focus:text-black max-md:focus-within:outline-none',
            className,
          )}
          onChange={validateChange}
          min={Math.ceil(min as number)}
          max={Math.ceil(max as number)}
        />
        <div
          className={cn(
            'pointer-events-none whitespace-nowrap text-sm transition-all peer-focus-within:hidden',
            displayValue?.at(0)?.type === 'currency' && 'absolute mx-auto',
            displayValue?.at(-1)?.type === 'currency' && 'absolute mx-auto',
          )}>
          {displayValue?.map((display) => (
            <span key={`${display.value}-${Math.random()}`}>
              {display.value}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
